import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/LangContext";
import { ShareButton } from "./ShareButton";

// Build a compact human-readable body for the lottery share sheet:
//   "Lotto 5 — 08 20 28 29 33 · Evening · 2026-07-18"
// Numbers are joined with a single space so the WhatsApp preview stays
// readable on one line even for 5-number draws. Kept as a local helper
// (not exported) — no other surface currently shares lottery results.
function _buildLotteryShareText(game, latest, t) {
  const nums = (latest?.numbers && latest.numbers.length)
    ? latest.numbers.join(" ")
    : (latest?.prizes || []).join(" ");
  const parts = [`${game.game_name} — ${nums}`];
  if (latest?.draw_time) {
    parts.push(t(`lottery_draw_${latest.draw_time}`));
  }
  if (latest?.draw_date) parts.push(latest.draw_date);
  return parts.join(" · ");
}

// Session 4 — Island Essentials strip. Sits between the Business &
// Tourism Spotlight and the main article feed. Horizontal scrollable
// row of 5 utility cards. When the user is on "All Islands" the strip
// defaults to Aruba data and shows an island-flag pill row that lets
// the user toggle between islands without changing the surrounding
// article-feed island context.
const ISLAND_PILLS = [
  { slug: "aruba",     name: "Aruba",       flag: "🇦🇼" },
  { slug: "curacao",   name: "Curaçao",     flag: "🇨🇼" },
  { slug: "bonaire",   name: "Bonaire",     flag: "🇧🇶" },
  { slug: "stmaarten", name: "St. Maarten", flag: "🇸🇽" },
];

const mapsUrl = (name, address) => {
  const q = encodeURIComponent([name, address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

export const IslandEssentials = ({ island = "all" }) => {
  const { t, timeAgo } = useT();
  const showsPills = island === "all";
  const [innerIsland, setInnerIsland] = useState(
    showsPills ? "aruba" : island
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep innerIsland in sync when the parent island changes
  useEffect(() => {
    setInnerIsland(island === "all" ? "aruba" : island);
  }, [island]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/essentials", { params: { island: innerIsland } })
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [innerIsland]);

  // Cheap string transform — memoisation would be a wash and would trip the
  // react-deps audit (timeAgo is a fresh closure every render — see
  // scripts/audit_react_deps.py).
  const updatedAgo = data?.updated_at ? timeAgo(data.updated_at) : "";

  return (
    <section className="mb-8" data-testid="island-essentials">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            style={{ width: 3, height: 20, background: "#0AADA0", display: "inline-block" }}
          />
          <span
            className="uppercase text-[#0AADA0]"
            style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 600 }}
          >
            {t("essentials_title")}
          </span>
        </div>
        {updatedAgo && (
          <span style={{ fontSize: 10, color: "#6B7C82" }} data-testid="essentials-updated">
            {t("essentials_updated_prefix")} {updatedAgo}
          </span>
        )}
      </div>
      <div className="border-t border-[#EEEEEE] mb-4" aria-hidden="true" />

      {/* Island flag pills — only when parent shows All Islands */}
      {showsPills && (
        <div
          className="flex flex-wrap gap-1.5 mb-4"
          data-testid="essentials-island-pills"
        >
          {ISLAND_PILLS.map((p) => {
            const active = innerIsland === p.slug;
            return (
              <button
                key={p.slug}
                onClick={() => setInnerIsland(p.slug)}
                data-testid={`essentials-pill-${p.slug}`}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${
                  active
                    ? "bg-[#0AADA0] text-white"
                    : "bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#EEEEEE]"
                }`}
              >
                <span>{p.flag}</span>
                <span>{p.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Horizontally scrollable card row. All widgets render inline —
         no section-level collapse (reverted 2026-02-22 per Calvin).
         Individual widgets keep their own internal toggles (e.g. Aruba
         lottery's "Show all 8 games"). */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
        data-testid="essentials-scroller"
      >
        {(loading || (data?.lottery && data.lottery.games && data.lottery.games.length > 0)) && (
          <LotteryCard   d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        )}
        <CurrencyCard   d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        {(loading || (data?.fuel && data.fuel.gasoline_price && data.fuel.diesel_price)) && (
          <FuelCard      d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        )}
        {/* Show pharmacy card if we have real data OR the safety
            fallback (2026-08-05) OR a static info card (Bonaire,
            Sint Maarten — added 2026-02-07). The fallback exists
            specifically so readers get an accurate emergency contact
            when no valid rotation is available — hiding it defeats
            the whole point. Info cards similarly render their own
            "how it actually works" content for islands with no
            rotation model at all. */}
        {(loading || (data?.pharmacy && (data.pharmacy.name || data.pharmacy.fallback || data.pharmacy.info_card))) && (
          <PharmacyCard  d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        )}
        {(loading || data?.cruise) && (
          <CruiseCard     d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        )}
        {(loading || (data?.beach && (data.beach.wave_height || data.beach.wind || data.beach.water_temp))) && (
          <SeaStateCard  d={data} loading={loading} timeAgo={timeAgo} island={innerIsland} />
        )}
        {(loading || data?.astro) && (
          <SunUvCard      d={data} loading={loading} timeAgo={timeAgo} />
        )}
      </div>
    </section>
  );
};

// --------- Base card wrapper ---------
const CardShell = ({ icon, label, labelKey, children, sponsor, testId, updated, timeAgo }) => {
  const { t } = useT();
  const displayLabel = labelKey ? t(labelKey) : label;
  return (
  <div
    className="bg-white border border-[#EEEEEE] flex-shrink-0 flex flex-col"
    style={{
      borderRadius: 12,
      padding: 16,
      minWidth: 280,
      flexBasis: 280,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      scrollSnapAlign: "start",
    }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-2">
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span
        className="uppercase text-[#0AADA0]"
        style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 600 }}
      >
        {displayLabel}
      </span>
    </div>
    <div className="flex-1 flex flex-col">{children}</div>
    {updated && (
      <div style={{ fontSize: 10, color: "#6B7C82", marginTop: 8 }}>
        {t("essentials_updated_prefix")} {timeAgo(updated)}
      </div>
    )}
    {sponsor && (
      <div
        style={{ fontSize: 10, color: "#6B7C82", fontStyle: "italic", marginTop: 4 }}
        data-testid={`${testId}-sponsor`}
      >
        {t("brought_to_you_by")} {sponsor}
      </div>
    )}
  </div>
  );
};

const NotAvailable = () => {
  const { t } = useT();
  return (
  <div
    className="text-center flex-1 flex items-center justify-center"
    style={{ fontSize: 12, color: "#6B7C82" }}
  >
    {t("essentials_not_available")}
  </div>
  );
};

// --------- Lottery Results ---------
// Sits FIRST in the Island Essentials strip. Renders every lottery
// covered for the current island, distinct blocks per game, with a
// "View past results" toggle that inlines the recent draw history.
// Type = "image" (Robbie's / Soualiga) embeds the source images with
// attribution rather than fabricating numbers.
// Aruba is a special case: 24ora publishes 8 games per draw session
// (Catochi / Big 4 / 1-OFF / Zodiac / Lucky / Lotto di Dia / Lotto 5 /
// Mini Mega), which — if all stacked vertically by default — pushes
// every downstream Essentials widget far below the fold. We surface
// only Lotto 5 (the flagship Fundacion Lotto pa Deporte weekly draw)
// + 1-OFF (twice-daily cadence) by default and inline-expand the rest
// via a "Show all N games" pill. Other islands have ≤2 games each so
// they render straight through unchanged.
const ARUBA_DEFAULT_KEYS = ["lotto_5", "one_off"];

const LotteryCard = ({ d, loading, timeAgo, island }) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const games = d?.lottery?.games || [];
  const isAruba = (island || "").toLowerCase() === "aruba";
  if (loading && games.length === 0) {
    return (
      <CardShell
        icon="🎰"
        labelKey="lottery_title"
        testId="essentials-card-lottery"
        timeAgo={timeAgo}
      >
        <div style={{ fontSize: 12, color: "#6B7C82" }}>{t("loading")}</div>
      </CardShell>
    );
  }
  if (!games.length) return null;
  const updated = games.find((g) => g?.latest?.scraped_at)?.latest?.scraped_at;

  // Split Aruba games into default (Lotto 5 + 1-OFF, in that order) and
  // hidden. Order matters — we render Lotto 5 first for prominence.
  let defaultGames = games;
  let hiddenGames = [];
  if (isAruba && games.length > ARUBA_DEFAULT_KEYS.length) {
    const byKey = Object.fromEntries(games.map((g) => [g.game_key, g]));
    defaultGames = ARUBA_DEFAULT_KEYS
      .map((k) => byKey[k])
      .filter(Boolean);
    hiddenGames = games.filter((g) => !ARUBA_DEFAULT_KEYS.includes(g.game_key));
    // Defensive fallback: if 24ora ever stops publishing Lotto 5 or
    // 1-OFF we still show *something* rather than a headless card.
    if (defaultGames.length === 0) {
      defaultGames = games.slice(0, 2);
      hiddenGames = games.slice(2);
    }
  }

  const visibleGames = expanded ? [...defaultGames, ...hiddenGames] : defaultGames;

  return (
    <CardShell
      icon="🎰"
      labelKey="lottery_title"
      testId="essentials-card-lottery"
      updated={updated}
      timeAgo={timeAgo}
    >
      <div className="flex flex-col gap-2.5" style={{ width: "100%", minWidth: 0 }}>
        {visibleGames.map((g, idx) => (
          <LotteryGame
            key={g.game_key}
            game={g}
            island={island}
            isLast={idx === visibleGames.length - 1 && hiddenGames.length === 0}
          />
        ))}
        {hiddenGames.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="lottery-toggle-all-games"
            className="text-[#0AADA0] hover:text-[#088F84] text-[12px] font-medium text-left pt-1 transition-colors"
            style={{ minHeight: 32 }}
          >
            {expanded
              ? t("lottery_show_less")
              : t("lottery_show_all").replace("{count}", String(games.length))}
          </button>
        )}
      </div>
    </CardShell>
  );
};

// One game block inside the LotteryCard. Handles text + image types
// and toggles an inline "past results" panel.
const LotteryGame = ({ game, island, isLast }) => {
  const { t } = useT();
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const latest = game?.latest;
  const available = !!game?.available && !!latest;

  const loadHistory = () => {
    if (history !== null) { setShowHistory(!showHistory); return; }
    setShowHistory(true);
    api
      .get("/essentials/lottery/history", {
        params: { island, game: game.game_key, limit: 10 },
      })
      .then((r) => setHistory(r?.data?.draws || []))
      .catch(() => setHistory([]));
  };

  const attr = latest?.attribution || {};
  const nums = (latest?.numbers && latest.numbers.length)
    ? latest.numbers
    : (latest?.prizes || []);

  return (
    <div
      className={isLast ? "" : "pb-2.5 border-b border-[#F0F0F0]"}
      id={`lottery-${game.game_key}`}
      data-testid={`lottery-game-${game.game_key}`}
    >
      <div className="mb-1 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div
            style={{ fontSize: 13, fontWeight: 600, color: "#1A2E35",
                     lineHeight: 1.2 }}
            data-testid={`lottery-game-${game.game_key}-name`}
          >
            {game.game_name}
          </div>
          {available && latest?.draw_date && (
            <div
              style={{ fontSize: 10, color: "#6B7C82", marginTop: 2,
                       fontVariantNumeric: "tabular-nums" }}
              data-testid={`lottery-game-${game.game_key}-meta`}
            >
              {latest.draw_date}
              {latest.draw_time ? ` · ${t(`lottery_draw_${latest.draw_time}`)}` : ""}
              {latest.draw_number ? ` · #${latest.draw_number}` : ""}
            </div>
          )}
        </div>
        {available && latest && (
          <ShareButton
            testId={`lottery-share-${game.game_key}`}
            compact
            title={`${game.game_name} — IslandsDaily`}
            text={_buildLotteryShareText(game, latest, t)}
            url={`${window.location.origin}/${island || "aruba"}#lottery-${game.game_key}`}
          />
        )}
      </div>

      {!available && (
        <div
          style={{ fontSize: 12, color: "#6B7C82", fontStyle: "italic",
                   padding: "6px 0", lineHeight: 1.35,
                   wordBreak: "break-word", overflowWrap: "anywhere",
                   whiteSpace: "normal" }}
          data-testid={`lottery-game-${game.game_key}-gap`}
        >
          {t("lottery_unavailable")}
        </div>
      )}

      {available && latest?.type === "image" && (
        <div
          className="flex gap-1.5 overflow-x-auto pb-1"
          data-testid={`lottery-game-${game.game_key}-images`}
        >
          {(latest?.media?.images || []).slice(0, 4).map((im, i) => (
            <a
              key={i}
              href={attr.source_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flexShrink: 0 }}
            >
              <img
                src={im.url}
                alt={im.alt || game.game_name}
                loading="lazy"
                style={{
                  height: 60,
                  width: "auto",
                  border: "1px solid #EEEEEE",
                  borderRadius: 4,
                }}
              />
            </a>
          ))}
        </div>
      )}

      {available && latest?.type !== "image" && nums.length > 0 && (
        <div
          className="flex flex-wrap gap-1"
          data-testid={`lottery-game-${game.game_key}-numbers`}
        >
          {nums.map((n, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 26,
                height: 26,
                padding: "0 6px",
                background: "#0AADA0",
                color: "white",
                borderRadius: 4,
                fontWeight: 700,
                fontSize: 12,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {n}
            </span>
          ))}
          {latest?.extra?.super_ball && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 26,
                height: 26,
                padding: "0 6px",
                background: "#E63946",
                color: "white",
                borderRadius: 4,
                fontWeight: 700,
                fontSize: 12,
                fontVariantNumeric: "tabular-nums",
              }}
              title={t("lottery_super_ball")}
              data-testid={`lottery-game-${game.game_key}-superball`}
            >
              {latest.extra.super_ball}
            </span>
          )}
        </div>
      )}

      {available && (latest?.type === "image" ? true : nums.length > 0) && (
        <>
          <button
            onClick={loadHistory}
            data-testid={`lottery-game-${game.game_key}-toggle-history`}
            style={{
              marginTop: 6,
              fontSize: 11,
              color: "#0AADA0",
              textDecoration: "underline",
              cursor: "pointer",
              background: "transparent",
              border: 0,
              padding: 0,
              textAlign: "left",
            }}
          >
            {showHistory ? t("lottery_hide_past") : t("lottery_view_past")}
          </button>
          {showHistory && (
            <div
              className="mt-2 pt-2 border-t border-[#F5F5F5]"
              data-testid={`lottery-game-${game.game_key}-history`}
            >
              {history === null && (
                <div style={{ fontSize: 11, color: "#6B7C82" }}>{t("loading")}</div>
              )}
              {history !== null && history.length === 0 && (
                <div style={{ fontSize: 11, color: "#6B7C82", fontStyle: "italic" }}>
                  {t("lottery_no_history")}
                </div>
              )}
              {history !== null && history.length > 0 && (
                <ul className="flex flex-col gap-1.5" style={{ margin: 0, padding: 0 }}>
                  {history.map((h, i) => {
                    const hNums = (h.numbers && h.numbers.length) ? h.numbers : (h.prizes || []);
                    return (
                      <li
                        key={i}
                        style={{ fontSize: 11, listStyle: "none",
                                 display: "flex", justifyContent: "space-between",
                                 gap: 8, alignItems: "baseline" }}
                      >
                        <span style={{ color: "#6B7C82", flexShrink: 0 }}>
                          {h.draw_date}
                          {h.draw_time ? ` · ${t(`lottery_draw_${h.draw_time}`)}` : ""}
                        </span>
                        <span
                          style={{
                            color: "#1A2E35",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                          }}
                        >
                          {hNums.join(" · ")}
                          {h.extra?.super_ball ? ` + ${h.extra.super_ball}` : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {available && attr?.source_name && (
        <div style={{ fontSize: 10, color: "#6B7C82", marginTop: 6 }}>
          {t("lottery_source")}:{" "}
          <a
            href={attr.source_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#6B7C82", textDecoration: "underline" }}
          >
            {attr.source_name}
          </a>
        </div>
      )}
    </div>
  );
};

// --------- Pharmacy ---------
// Formats +599/+297/+1 style phone numbers into a tel:-safe href
// by stripping whitespace only. Preserves the leading + and country
// code so click-to-call works on both mobile Safari and Android.
const telHref = (phone) => `tel:${(phone || "").replace(/\s+/g, "")}`;

// PharmacyInfoCard — static info card for islands that don't run an
// on-duty rotation (Bonaire, Sint Maarten). Reads its copy keys from
// the payload so the same component renders both variants with each
// island's specific framing note + section headings. Handles two
// pharmacy-list shapes:
//   • `regular_hours[]` (Bonaire)   — name / district / phone / addr
//   • `extended_hours[]` (SXM)      — same fields + per-pharmacy hours
// Also renders `after_hours[]` (Huisartsenpost/ED contact rows) and
// the `emergency` (911) block. Every phone is a tel: link.
const PharmacyInfoCard = ({ p, t, lang: _lang }) => {
  const regularHours  = Array.isArray(p.regular_hours)  ? p.regular_hours  : [];
  const extendedHours = Array.isArray(p.extended_hours) ? p.extended_hours : [];
  const pharmacies    = regularHours.length ? regularHours : extendedHours;
  const showsHoursColumn = extendedHours.length > 0;
  const afterHours    = Array.isArray(p.after_hours)    ? p.after_hours    : [];
  const emergency     = p.emergency;
  const listHeadingKey = p.regular_hours_heading_key
                      || p.extended_hours_heading_key;
  // Collapse/expand — see comment on essentials_pharmacy_show_all in
  // i18n.js. Info cards render 4+ pharmacies with hours/phone/address
  // inline; at full height they run ~800px, dwarfing sibling essentials
  // cards. Default now shows framing note + first pharmacy + a summary
  // row + expand button; user opts into the full list.
  const [expanded, setExpanded] = useState(false);
  const collapsibleThreshold = 1; // Show 1 pharmacy in collapsed state
  const isCollapsible = pharmacies.length > collapsibleThreshold;
  const visiblePharmacies = expanded || !isCollapsible
    ? pharmacies
    : pharmacies.slice(0, collapsibleThreshold);
  const hiddenCount = pharmacies.length - visiblePharmacies.length;
  return (
    <div className="flex flex-col gap-3" data-testid="pharmacy-info-card">
      {p.note_key && (
        <div
          data-testid="pharmacy-info-note"
          style={{
            fontSize: 12, color: "#4A5A61", lineHeight: 1.5,
            background: "#F7F9FA", padding: "8px 10px",
            borderRadius: 8, borderLeft: "3px solid #0AADA0",
          }}
        >
          {t(p.note_key)}
        </div>
      )}

      {pharmacies.length > 0 && (
        <div data-testid="pharmacy-info-list">
          {listHeadingKey && (
            <div
              className="uppercase"
              style={{
                fontSize: 10, letterSpacing: "0.06em",
                color: "#0AADA0", fontWeight: 600, marginBottom: 6,
              }}
              data-testid="pharmacy-info-list-heading"
            >
              {t(listHeadingKey)}
            </div>
          )}
          <div className="flex flex-col gap-2.5">
            {visiblePharmacies.map((ph, i) => (
              <div
                key={`ph-${i}`}
                data-testid={`pharmacy-info-item-${i}`}
                style={{
                  paddingTop: i > 0 ? 10 : 0,
                  borderTop: i > 0 ? "1px solid #EEE" : "none",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500, color: "#1A2E35" }}>
                  {ph.name}
                </div>
                {ph.address && (
                  <div style={{ fontSize: 11, color: "#6B7C82", marginTop: 2 }}>
                    {ph.address}
                  </div>
                )}
                {showsHoursColumn && ph.hours && (
                  <div
                    style={{ fontSize: 12, color: "#1A2E35", marginTop: 4, fontWeight: 500 }}
                    data-testid={`pharmacy-info-item-hours-${i}`}
                  >
                    {p.hours_column_label_key ? (
                      <span style={{ color: "#6B7C82", fontWeight: 400 }}>
                        {t(p.hours_column_label_key)}:{" "}
                      </span>
                    ) : null}
                    {ph.hours}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-1.5">
                  {ph.phone && (
                    <a
                      href={telHref(ph.phone)}
                      style={{ fontSize: 12, color: "#0AADA0", textDecoration: "none" }}
                      data-testid={`pharmacy-info-item-phone-${i}`}
                    >
                      📞 {ph.phone}
                    </a>
                  )}
                  <a
                    href={mapsUrl(ph.name, ph.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, color: "#E63946", fontWeight: 600 }}
                    data-testid={`pharmacy-info-item-directions-${i}`}
                  >
                    {t("essentials_directions") || "Directions"} →
                  </a>
                </div>
              </div>
            ))}
          </div>
          {isCollapsible && !expanded && hiddenCount > 0 && (
            <div
              style={{
                fontSize: 11, color: "#6B7C82", marginTop: 8,
                paddingTop: 8, borderTop: "1px dashed #E5E5E5",
              }}
              data-testid="pharmacy-info-more-summary"
            >
              + {hiddenCount} {t("essentials_pharmacy_more_pharmacies")}
            </div>
          )}
        </div>
      )}

      {expanded && afterHours.length > 0 && p.after_hours_heading_key && (
        <div data-testid="pharmacy-info-after-hours">
          <div
            className="uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.06em",
              color: "#E63946", fontWeight: 600, marginBottom: 6,
              paddingTop: 4, borderTop: "1px dashed #E5E5E5",
            }}
            data-testid="pharmacy-info-after-hours-heading"
          >
            {t(p.after_hours_heading_key)}
          </div>
          <div className="flex flex-col gap-2">
            {afterHours.map((ah, i) => (
              <div key={`ah-${i}`} data-testid={`pharmacy-info-ah-${i}`}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "#1A2E35" }}>
                  {t(ah.label_key)}
                </div>
                {ah.hours_key && (
                  <div style={{ fontSize: 11, color: "#6B7C82", marginTop: 1 }}>
                    {t(ah.hours_key)}
                  </div>
                )}
                {ah.phone && (
                  <a
                    href={telHref(ah.phone)}
                    style={{ fontSize: 12, color: "#0AADA0", textDecoration: "none", marginTop: 2, display: "inline-block" }}
                    data-testid={`pharmacy-info-ah-phone-${i}`}
                  >
                    📞 {ah.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {emergency && emergency.phone && (
        <div
          data-testid="pharmacy-info-emergency"
          style={{
            marginTop: 2, padding: "6px 10px",
            background: "#FEF2F3", borderRadius: 8,
            borderLeft: "3px solid #E63946",
          }}
        >
          <div style={{ fontSize: 11, color: "#4A5A61" }}>
            {t(emergency.label_key)}
          </div>
          <a
            href={telHref(emergency.phone)}
            style={{ fontSize: 15, fontWeight: 700, color: "#E63946", textDecoration: "none" }}
            data-testid="pharmacy-info-emergency-phone"
          >
            📞 {emergency.phone}
          </a>
        </div>
      )}

      {isCollapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="pharmacy-info-toggle"
          style={{
            marginTop: 4,
            alignSelf: "flex-start",
            background: "transparent",
            border: "1px solid #E5E5E5",
            color: "#0AADA0",
            fontSize: 12,
            fontWeight: 600,
            padding: "4px 10px",
            borderRadius: 999,
            cursor: "pointer",
          }}
        >
          {expanded
            ? t("essentials_pharmacy_show_less")
            : t("essentials_pharmacy_show_all")}
          {" "}
          <span aria-hidden style={{ marginLeft: 2 }}>{expanded ? "▲" : "▼"}</span>
        </button>
      )}
    </div>
  );
};

const PharmacyCard = ({ d, loading, timeAgo, island }) => {
  const { lang, t } = useT();
  const p = d?.pharmacy;
  const sp = d?.sponsors?.pharmacy;
  const zones = Array.isArray(p?.zones) ? p.zones.filter(z => z && z.name) : [];
  const multi = zones.length > 1;
  const isFallback = !!p?.fallback;
  // District label (2026-08-06): each pharmacy row now carries a
  // `district` field hydrated from the per-pharmacy directory row.
  // We prefer the district (Oranjestad / San Nicolas / Noord / etc.)
  // — it's the label a local would actually use to orient someone.
  // If a zone has no district yet (e.g. a future island whose
  // directory hasn't been populated, or a pharmacy that isn't in the
  // directory at all), fall back to the legacy Papiamento →
  // English/NL/ES map for the two known Aruba Botica zones, then
  // finally fall back to the raw `zone` string. This ordering keeps
  // the widget working during any pre-seed transitional period.
  const zoneLabel = (z) => {
    if (!z) return "";
    // Papiamento view keeps the original Papiamento zone label —
    // "Pabou/Pariba di Brug" reads more natural to PA speakers than
    // the English district names. District wins if present because
    // it applies uniformly across all four languages.
    if (z.district) return z.district;
    const zone = z.zone || "";
    if (lang && lang.startsWith("pap")) return zone;
    const map = { "Pabou di Brug": "Oranjestad", "Pariba di Brug": "San Nicolas" };
    return map[zone] || zone;
  };
  return (
    <CardShell
      icon="💊"
      labelKey="essentials_pharmacy"
      testId="essentials-card-pharmacy"
      sponsor={sp}
      updated={p?.updated_at}
      timeAgo={timeAgo}
    >
      {loading ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>Loading…</div>
      ) : p?.info_card ? (
        // Static info card (Bonaire, Sint Maarten). Renders regular-
        // hours / extended-hours pharmacy list + after-hours contacts,
        // instead of the Aruba/Curaçao rotation model. All strings
        // come from i18n via the `*_key` fields on the payload so the
        // card is fully localized in EN/NL/ES/pap/pap_aw.
        <PharmacyInfoCard p={p} t={t} lang={lang} />
      ) : isFallback ? (
        // Health-safety fallback (2026-08-05). Rendered when we have NO
        // currently-valid on-duty pharmacy row for today. Never show
        // expired scraped data as if it were current — an out-of-date
        // pharmacy card can direct someone to a closed pharmacy in a
        // medical emergency. Instead point readers at the hospital's
        // 24/7 switchboard, which always has the accurate answer.
        <div data-testid="pharmacy-fallback">
          <div style={{ fontSize: 12, color: "#6B7C82", marginBottom: 6, lineHeight: 1.4 }}>
            {p.fallback_note}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#1A2E35", marginBottom: 6 }}>
            {p.fallback_label}
          </div>
          <a
            href={`tel:${(p.fallback_phone || "").replace(/\s+/g, "")}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md"
            style={{
              fontSize: 13, fontWeight: 600,
              background: "#0AADA0", color: "white", textDecoration: "none",
            }}
            data-testid="pharmacy-fallback-call"
          >
            📞 {p.fallback_phone}
          </a>
        </div>
      ) : !p || !p.name ? (
        <NotAvailable />
      ) : multi ? (
        <div className="flex flex-col gap-3" data-testid="pharmacy-zones">
          {p.duty_status && (
            <div
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded self-start"
              style={{
                fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
                background: p.duty_status === "on_duty_now" ? "#0AADA0" : "#F1F3F5",
                color: p.duty_status === "on_duty_now" ? "white" : "#4A5A61",
              }}
              data-testid="pharmacy-duty-status"
            >
              {p.duty_status === "on_duty_now" && (
                <span
                  style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: "white", display: "inline-block",
                  }}
                />
              )}
              {p.duty_status === "on_duty_now"
                ? t("essentials_duty_on_now")
                : t("essentials_duty_starts_tonight")}
            </div>
          )}
          {zones.map((z, i) => (
            <div
              key={`${z.zone}-${i}`}
              className="pt-2"
              style={{ borderTop: i > 0 ? "1px solid #EEE" : "none" }}
              data-testid={`pharmacy-zone-${i}`}
            >
              {z.zone && (
                <div
                  className="uppercase"
                  style={{ fontSize: 10, letterSpacing: "0.06em", color: "#0AADA0", fontWeight: 600 }}
                >
                  {zoneLabel(z)}
                </div>
              )}
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1A2E35", marginTop: 2 }}>
                {z.name}
              </div>
              {z.address && (
                <div style={{ fontSize: 11, color: "#6B7C82", marginTop: 2 }}>
                  {z.address}
                </div>
              )}
              <div className="flex items-center gap-3 mt-1">
                {z.phone && (
                  <a
                    href={`tel:${z.phone}`}
                    style={{ fontSize: 12, color: "#0AADA0", textDecoration: "none" }}
                    data-testid={`pharmacy-zone-phone-${i}`}
                  >
                    📞 {z.phone}
                  </a>
                )}
                <a
                  href={mapsUrl(z.name, z.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: "#E63946", fontWeight: 600 }}
                  data-testid={`pharmacy-zone-directions-${i}`}
                >
                  {t("essentials_directions") || "Directions"} →
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#1A2E35", marginBottom: 4 }}>
            {p.name}
          </div>
          <div style={{ fontSize: 12, color: "#6B7C82", marginBottom: 6 }}>
            {p.address}
          </div>
          {p.phone && (
            <a
              href={`tel:${p.phone}`}
              style={{ fontSize: 12, color: "#0AADA0", textDecoration: "none" }}
              data-testid="pharmacy-phone"
            >
              📞 {p.phone}
            </a>
          )}
          <div style={{ fontSize: 12, color: "#6B7C82", marginTop: 4 }}>
            {p.is_24h
              ? "Open 24 hours"
              : p.close_time
              ? `Open until ${p.close_time}`
              : ""}
          </div>
          <a
            href={mapsUrl(p.name, p.address)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#E63946", fontWeight: 600, marginTop: 8 }}
            data-testid="pharmacy-directions"
          >
            {t("essentials_directions") || "Directions"} →
          </a>
        </>
      )}
    </CardShell>
  );
};

// --------- Sea State (formerly "Beach Conditions") ---------
// Rescoped 2026-07-18: dropped flag-color safety-recommendation language
// (green/yellow/red/black) because Open-Meteo Marine is a forecast model,
// not lifeguard data, and a single wave-height threshold cannot represent
// real per-beach safety. Now shows waves + wind + water temp only, with
// explicit attribution. Admin can still post free-form advisory text via
// the manual override.

const SeaStateCard = ({ d, loading, timeAgo, island }) => {
  const { t } = useT();
  const b = d?.beach;
  const sp = d?.sponsors?.beach;
  const advisory = b?.override && b?.condition ? b.condition : "";
  const hasData = b && (b.wave_height || b.wind || b.water_temp || advisory);
  return (
    <CardShell
      icon="🌊"
      labelKey="essentials_beach"
      testId="essentials-card-beach"
      sponsor={sp}
      updated={b?.fetched_at || b?.updated_at}
      timeAgo={timeAgo}
    >
      {loading ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>Loading…</div>
      ) : !hasData ? (
        <NotAvailable />
      ) : (
        <>
          {advisory && (
            <div
              data-testid="essentials-sea-advisory"
              style={{
                fontSize: 12,
                color: "#7F1D1D",
                background: "#FEE2E2",
                border: "1px solid #FCA5A5",
                borderRadius: 6,
                padding: "6px 8px",
                marginBottom: 6,
              }}
            >
              <strong>{t("essentials_local_advisory")}:</strong> {advisory}
            </div>
          )}
          {b.wave_height && (
            <div style={{ fontSize: 14, color: "#1A2E35", marginBottom: 2 }}>
              <strong>{t("essentials_waves")}:</strong> {b.wave_height}
            </div>
          )}
          {b.wind && (
            <div style={{ fontSize: 13, color: "#1A2E35", marginBottom: 2 }}>
              <strong>{t("essentials_wind")}:</strong> {b.wind}
            </div>
          )}
          {b.water_temp && (
            <div style={{ fontSize: 13, color: "#1A2E35", marginBottom: 2 }}>
              <strong>{t("essentials_water")}:</strong> {b.water_temp}
            </div>
          )}
          {b.best_beach && (
            <div style={{ fontSize: 12, color: "#6B7C82", marginBottom: 2 }}>
              Best today: {b.best_beach}
            </div>
          )}
          <div
            data-testid="essentials-sea-attribution"
            style={{
              fontSize: 10,
              color: "#6B7C82",
              marginTop: 6,
              paddingTop: 6,
              borderTop: "1px solid #F1F5F9",
              lineHeight: 1.4,
            }}
          >
            {t("essentials_beach_attribution")}
          </div>
        </>
      )}
    </CardShell>
  );
};

// --------- Currency ---------
const CURRENCY_FLAG = {
  USD: "🇺🇸", EUR: "🇪🇺", CAD: "🇨🇦", GBP: "🇬🇧",
  AWG: "🇦🇼", ANG: "🇨🇼",
};

// AnimatedNumber — counts up from 0 to `value` over 800ms with ease-out
// cubic. Only animates when `trigger` changes (starting from > 0). Preserves
// the original number of decimal places of `value`. Pure JS, no library.
const AnimatedNumber = ({ value, trigger }) => {
  const decimals = (() => {
    const s = String(value);
    const i = s.indexOf(".");
    return i === -1 ? 0 : s.length - i - 1;
  })();
  const [display, setDisplay] = useState(trigger > 0 ? 0 : value);
  const rafRef = useRef(null);

  useEffect(() => {
    if (trigger === 0) {
      setDisplay(value);
      return undefined;
    }
    const start = performance.now();
    const duration = 800;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(value * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(value);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [trigger, value]);

  return <>{Number(display).toFixed(decimals)}</>;
};

const CurrencyCard = ({ d, loading, timeAgo, island }) => {
  const { t } = useT();
  const c = d?.currency;
  const sp = d?.sponsors?.currency;
  const base = c?.base_currency;
  const display = c?.display || [];
  const rates = c?.rates || {};
  const unavailable = !c || c.unavailable || Object.keys(rates).length === 0;

  // Fresh-fetch animation: increments only when new (non-cached) rate data
  // arrives. Cached data does NOT trigger animation.
  const [freshVersion, setFreshVersion] = useState(0);
  const [pulsing, setPulsing] = useState(false);
  const lastUpdatedRef = useRef(null);

  useEffect(() => {
    if (!c || c.unavailable) return;
    if (c.is_cached) return;
    if (lastUpdatedRef.current === c.last_updated) return;
    lastUpdatedRef.current = c.last_updated;
    setFreshVersion((v) => v + 1);
  }, [c?.last_updated, c?.is_cached, c?.unavailable]);

  // After the 800ms counter animation completes, briefly pulse rate rows.
  useEffect(() => {
    if (freshVersion === 0) return undefined;
    const startT = setTimeout(() => setPulsing(true), 800);
    const endT = setTimeout(() => setPulsing(false), 1200);
    return () => {
      clearTimeout(startT);
      clearTimeout(endT);
    };
  }, [freshVersion]);

  // Mini converter state
  const [amount, setAmount] = useState("1");
  const [selected, setSelected] = useState(display[0] || "USD");
  useEffect(() => {
    if (display.length && !display.includes(selected)) {
      setSelected(display[0]);
    }
  }, [display, selected]);

  const converted = (() => {
    const n = parseFloat(amount || "0");
    if (isNaN(n)) return null;
    const r = rates[selected];
    if (!r) return null;
    // Aruba (base=AWG): row shows "1 {code} = r AWG" → n {code} = n*r AWG
    // Others (base=USD): row shows "1 USD = r {code}" → n USD = n*r {code}
    return (n * r).toFixed(2);
  })();

  const converterFrom = base === "AWG" ? selected : "USD";
  const converterTo = base === "AWG" ? "AWG" : selected;

  return (
    <CardShell
      icon="💱"
      labelKey="essentials_currency"
      testId="essentials-card-currency"
      sponsor={sp}
      updated={c?.last_updated}
      timeAgo={timeAgo}
    >
      {loading ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>Loading…</div>
      ) : unavailable ? (
        <div
          className="text-center flex-1 flex items-center justify-center"
          style={{ fontSize: 12, color: "#6B7C82" }}
          data-testid="currency-unavailable"
        >
          Rates unavailable
        </div>
      ) : (
        <>
          <style>{`
            @keyframes essentialsRatePulse {
              0%   { background-color: transparent; }
              50%  { background-color: rgba(10,173,160,0.08); }
              100% { background-color: transparent; }
            }
            .essentials-rate-pulse {
              animation: essentialsRatePulse 0.4s ease-out;
            }
          `}</style>
          <div className="space-y-1 mb-2">
            {display.map((code) => {
              const v = rates[code];
              if (v == null) return null;
              return (
                <div
                  key={code}
                  className={`flex items-center justify-between${pulsing ? " essentials-rate-pulse" : ""}`}
                  data-testid={`currency-row-${code}`}
                >
                  <span style={{ fontSize: 13, color: "#6B7C82" }}>
                    {CURRENCY_FLAG[code] || ""} {code}
                  </span>
                  <span style={{ fontSize: 13, color: "#1A2E35", fontWeight: 500 }}>
                    <AnimatedNumber value={v} trigger={freshVersion} />
                    {base === "AWG" ? " AWG" : ` ${code}`}
                  </span>
                </div>
              );
            })}
          </div>
          {c?.is_cached && (
            <div
              style={{ fontSize: 10, color: "#6B7C82" }}
              data-testid="currency-cached-notice"
            >
              Rates may be outdated
            </div>
          )}
          <div style={{ fontSize: 10, color: "#6B7C82", marginBottom: 2 }}>
            {t("essentials_rates_note")}
          </div>
          <div style={{ marginBottom: 6 }}>
            <a
              href="https://www.cbaruba.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 10, color: "#6B7C82", textDecoration: "underline" }}
              data-testid="currency-cba-attribution"
            >
              {t("essentials_source_prefix")}: Centrale Bank van Aruba (cbaruba.org)
            </a>
          </div>

          {/* Mini converter */}
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                border: "1px solid #EEEEEE",
                borderRadius: 8,
                padding: "0 12px",
                height: 40,
                width: "100%",
                fontSize: 13,
                minWidth: 0,
              }}
              data-testid="currency-converter-input"
            />
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                border: "1px solid #EEEEEE",
                borderRadius: 8,
                padding: "0 8px",
                height: 40,
                fontSize: 13,
                background: "white",
              }}
              data-testid="currency-converter-select"
            >
              {display.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          {converted !== null && (
            <div
              style={{ fontSize: 12, color: "#1A2E35", marginTop: 6 }}
              data-testid="currency-converter-output"
            >
              {amount || "0"} {converterFrom} = {converted} {converterTo}
            </div>
          )}
        </>
      )}
    </CardShell>
  );
};

// --------- Cruise ---------
const CruiseCard = ({ d, loading, timeAgo, island }) => {
  const { t } = useT();
  const c = d?.cruise;
  const sp = d?.sponsors?.cruise;
  const today = c?.today || [];
  const tomorrow = c?.tomorrow || [];
  const showing = today.slice(0, 3);
  const remaining = Math.max(0, today.length - showing.length);
  // Format "2026-08-04" → "4 Aug" — compact enough to fit inside the empty-state
  // one-liner without wrapping. Reuses the month array defined near the fuel
  // card so we stay consistent site-wide.
  const _formatCruiseDate = (iso) => {
    if (!iso || typeof iso !== "string") return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const day = parseInt(m[3], 10);
    const month = _FUEL_MONTHS_EN[parseInt(m[2], 10) - 1] || m[2];
    return `${day} ${month}`;
  };
  return (
    <CardShell
      icon="🚢"
      labelKey="essentials_cruise"
      testId="essentials-card-cruise"
      sponsor={sp}
      updated={d?.updated_at}
      timeAgo={timeAgo}
    >
      {loading ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>Loading…</div>
      ) : today.length === 0 ? (
        <div
          className="text-center flex-1 flex flex-col items-center justify-center gap-1 px-2"
          data-testid="cruise-empty"
        >
          {c?.next_scheduled ? (
            <>
              <div style={{ fontSize: 13, color: "#6B7C82" }}>
                {t("essentials_cruise_no_ships_today")}
              </div>
              <div
                style={{ fontSize: 12, color: "#1A2E35", fontWeight: 500 }}
                data-testid="cruise-next-scheduled"
              >
                {t("essentials_cruise_next_scheduled")
                  .replace(
                    "{ship}",
                    (Array.isArray(c.next_scheduled_ships) &&
                     c.next_scheduled_ships.length > 0
                      ? c.next_scheduled_ships
                          .map((s) => s.ship_name)
                          .filter(Boolean)
                          .join(" + ")
                      : c.next_scheduled.ship_name) || "—"
                  )
                  .replace("{date}", _formatCruiseDate(c.next_scheduled.date))}
              </div>
            </>
          ) : (
            <div
              style={{ fontSize: 13, color: "#6B7C82" }}
              data-testid="cruise-no-data"
            >
              {t("essentials_cruise_no_data")}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* 2026-02-06: Lead with the count so search snippets +
              at-a-glance readers get "N in port today" first, before
              the per-ship detail rows below. */}
          <div
            className="flex items-baseline gap-2 pb-1"
            data-testid="cruise-in-port-today-lead"
          >
            <span className="text-[20px] font-semibold text-[#0AADA0] leading-none">
              🚢 {today.length}
            </span>
            <span className="text-[12px] text-[#6B7C82]">
              {t("cruise_widget_in_port_label")}
            </span>
          </div>
          {showing.map((s, i) => (
            <div
              key={s.id || i}
              style={{
                paddingBottom: 6,
                borderBottom: i < showing.length - 1 ? "1px solid #EEEEEE" : "none",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1A2E35" }}>
                {s.ship_name}
              </div>
              <div style={{ fontSize: 12, color: "#6B7C82" }}>{s.cruise_line}</div>
              <div style={{ fontSize: 12, color: "#6B7C82" }}>
                Arrives {s.arrives || "—"} · Departs {s.departs || "—"}
              </div>
              {s.port_label && (
                <div
                  style={{ fontSize: 11, color: "#8A9AA1" }}
                  data-testid={`cruise-pier-${i}`}
                >
                  📍 {s.port_label}
                </div>
              )}
              <div style={{ fontSize: 12, color: "#0AADA0" }}>
                👥 {s.passengers} passengers
              </div>
            </div>
          ))}
          {remaining > 0 && (
            <div style={{ fontSize: 12, color: "#6B7C82" }}>and {remaining} more</div>
          )}
          <div style={{ fontSize: 12, fontWeight: 500, color: "#0AADA0", marginTop: 4 }}>
            Total today: {c.total_passengers} passengers
          </div>
        </div>
      )}
      {tomorrow.length > 0 && (
        <div
          style={{ fontSize: 11, color: "#6B7C82", fontStyle: "italic", marginTop: 6 }}
          data-testid="cruise-tomorrow"
        >
          Tomorrow: {tomorrow.map((s) => s.ship_name).filter(Boolean).join(", ")}
        </div>
      )}
      <Link
        to={`/${island === "stmaarten" ? "st-maarten" : island}/cruise-schedule`}
        style={{ fontSize: 12, color: "#E63946", fontWeight: 600, marginTop: 8 }}
        data-testid="cruise-full"
      >
        {t("essentials_cruise_full")}
      </Link>
      {c?.source === "cruisemapper" && today.length > 0 && (
        <div
          data-testid="cruise-attribution"
          style={{
            fontSize: 10,
            color: "#6B7C82",
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid #F1F5F9",
            lineHeight: 1.4,
          }}
        >
          {c.stale_days > 1 ? (
            <span style={{ color: "#92400E", fontWeight: 500 }}>
              {t("essentials_cruise_stale").replace("{n}", c.stale_days)} ·{" "}
            </span>
          ) : null}
          {t("essentials_cruise_attribution")}
        </div>
      )}
    </CardShell>
  );
};

// --------- Sun & UV Index ---------
// Sunrise, sunset, and peak UV — all pulled from Open-Meteo's daily
// forecast in a single call (`_load_astro` in server.py). UV Index uses
// WHO/EPA color bands. Only shown when the astro payload succeeds.
const SunUvCard = ({ d, loading, timeAgo }) => {
  const { t } = useT();
  const a = d?.astro;
  return (
    <CardShell
      icon="☀️"
      labelKey="essentials_sun"
      testId="essentials-card-sunuv"
      updated={a?.fetched_at}
      timeAgo={timeAgo}
    >
      {loading ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>Loading…</div>
      ) : !a ? (
        <NotAvailable />
      ) : (
        <>
          <div style={{ fontSize: 14, color: "#1A2E35", marginBottom: 4 }}>
            <strong>{t("essentials_sunrise")}:</strong> {a.sunrise || "—"}
            <span style={{ margin: "0 8px", color: "#CBD5E1" }}>·</span>
            <strong>{t("essentials_sunset")}:</strong> {a.sunset || "—"}
          </div>
          {a.uv_index_max !== null && a.uv_index_max !== undefined && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 14, color: "#1A2E35" }}>
                <strong>{t("essentials_uv_peak")}:</strong> {a.uv_index_max}
              </span>
              {a.uv_label && (
                <span
                  data-testid="uv-pill"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 9999,
                    color: "white",
                    background: a.uv_color || "#6B7C82",
                    letterSpacing: 0.3,
                  }}
                >
                  {t(`uv_level_${a.uv_label.toLowerCase().replace(/\s+/g, "_")}`) || a.uv_label}
                </span>
              )}
            </div>
          )}
          <div
            data-testid="sunuv-attribution"
            style={{
              fontSize: 10,
              color: "#6B7C82",
              marginTop: 8,
              paddingTop: 6,
              borderTop: "1px solid #F1F5F9",
              lineHeight: 1.4,
            }}
          >
            {t("essentials_sun_attribution")}
          </div>
        </>
      )}
    </CardShell>
  );
};

// --------- Fuel Prices ---------
// Government-published retail fuel prices for gasoline + diesel, scraped
// daily at 06:00 AST from each island's regulator/ministry publication
// (see /app/backend/fuel_scraper.py). Prices are refreshed only when a
// new publication is detected AND the extracted value passes the
// per-island plausibility guard, so this card can legitimately display
// values that are weeks or months old — the "As of" line is the source
// of truth for freshness. St. Maarten is not scraped (no reliable public
// source discovered) so the card simply doesn't render for that island.
const _FUEL_MONTHS_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const _formatFuelDate = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const day = parseInt(m[3], 10);
  const month = _FUEL_MONTHS_EN[parseInt(m[2], 10) - 1] || m[2];
  return `${day} ${month} ${m[1]}`;
};

const FuelCard = ({ d, loading, timeAgo, island }) => {
  const { t } = useT();
  const f = d?.fuel;
  const sp = d?.sponsors?.fuel;
  const hasData = f && !f.unavailable && f.gasoline_price && f.diesel_price;
  return (
    <CardShell
      icon="⛽"
      labelKey="essentials_fuel"
      testId="essentials-card-fuel"
      sponsor={sp}
      updated={f?.scraped_at}
      timeAgo={timeAgo}
    >
      {loading && !hasData ? (
        <div style={{ fontSize: 12, color: "#6B7C82" }}>{t("loading")}</div>
      ) : !hasData ? (
        <div
          className="text-center flex-1 flex items-center justify-center"
          style={{ fontSize: 12, color: "#6B7C82" }}
          data-testid="fuel-unavailable"
        >
          {t("essentials_fuel_unavailable")}
        </div>
      ) : (
        <>
          <div className="space-y-1.5 mb-2" data-testid="fuel-rows">
            <div
              className="flex items-center justify-between"
              data-testid="fuel-row-gasoline"
            >
              <span style={{ fontSize: 13, color: "#6B7C82" }}>
                {t("essentials_fuel_gasoline")}
              </span>
              <span
                style={{
                  fontSize: 14, color: "#1A2E35", fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {Number(f.gasoline_price).toFixed(2)} {f.currency}/L
              </span>
            </div>
            <div
              className="flex items-center justify-between"
              data-testid="fuel-row-diesel"
            >
              <span style={{ fontSize: 13, color: "#6B7C82" }}>
                {t("essentials_fuel_diesel")}
              </span>
              <span
                style={{
                  fontSize: 14, color: "#1A2E35", fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {Number(f.diesel_price).toFixed(2)} {f.currency}/L
              </span>
            </div>
          </div>
          <div
            data-testid="fuel-as-of"
            style={{ fontSize: 11, color: "#6B7C82", marginTop: 4 }}
          >
            {t("essentials_fuel_as_of").replace("{date}", _formatFuelDate(f.effective_date))}
          </div>
          {f.source_url && (
            <div
              data-testid="fuel-attribution"
              style={{
                fontSize: 10, color: "#6B7C82", marginTop: 6,
                paddingTop: 6, borderTop: "1px solid #F1F5F9", lineHeight: 1.4,
              }}
            >
              <a
                href={f.source_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#6B7C82", textDecoration: "underline" }}
              >
                {t("essentials_source_prefix")}
              </a>
              {" · "}
              {t("essentials_fuel_attribution")}
            </div>
          )}
        </>
      )}
    </CardShell>
  );
};

